import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  FileText, 
  Calendar, 
  BarChart3,
  Play,
  CheckCircle,
  AlertCircle,
  Timer,
  TrendingUp
} from 'lucide-react';
import { ClientDashboardLayout } from './ClientDashboardLayout';
import { TimeTrackerWidget } from '../../components/TimeTracker';
import { useTimeTracker } from '../../hooks/useTimeTracker';
import { useAuthStore, api } from '../auth/authStore';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface DashboardStats {
  todayHours: number;
  weekHours: number;
  monthHours: number;
  totalTasks: number;
  completedTasks: number;
  pendingTasks: number;
}

interface RecentTimeEntry {
  id: string;
  description: string;
  duration: number;
  date: string;
  taskTitle?: string;
}

interface Task {
  id: string;
  title: string;
  status: string;
  priority: string;
  dueDate?: string;
}

export const ClientDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const timeTracker = useTimeTracker(user?.userId);
  const navigate = useNavigate();
  
  const [stats, setStats] = useState<DashboardStats>({
    todayHours: 0,
    weekHours: 0,
    monthHours: 0,
    totalTasks: 0,
    completedTasks: 0,
    pendingTasks: 0
  });
  const [recentEntries, setRecentEntries] = useState<RecentTimeEntry[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch time stats
      const statsResponse = await api.get('/time-entries/stats');
      if (statsResponse.data.success) {
        setStats(prev => ({
          ...prev,
          ...statsResponse.data.data
        }));
      }

      // Fetch recent time entries
      const entriesResponse = await api.get('/time-entries/my-entries?limit=5');
      if (entriesResponse.data.success) {
        setRecentEntries(entriesResponse.data.data.entries || []);
      }

      // Fetch tasks (if available for clients)
      try {
        const tasksResponse = await api.get('/tasks/my-tasks?limit=10');
        if (tasksResponse.data.success) {
          const taskData = tasksResponse.data.data.tasks || [];
          setTasks(taskData);
          setStats(prev => ({
            ...prev,
            totalTasks: taskData.length,
            completedTasks: taskData.filter((t: Task) => t.status === 'COMPLETED').length,
            pendingTasks: taskData.filter((t: Task) => t.status !== 'COMPLETED').length
          }));
        }
      } catch (error) {
        // Tasks might not be available for clients
        console.log('Tasks not available for client');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'todo':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return 'text-red-600';
      case 'high':
        return 'text-orange-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <ClientDashboardLayout currentPage="dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </ClientDashboardLayout>
    );
  }

  return (
    <ClientDashboardLayout currentPage="dashboard">
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.firstName}!
          </h1>
          <p className="text-gray-600">
            Here's an overview of your time tracking and tasks.
          </p>
        </div>

        {/* Time Tracker Widget */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Time Tracker</h2>
          <TimeTrackerWidget
            userRole="Client"
            availableTasks={tasks}
            onTimerUpdate={() => fetchDashboardData()}
            compact={false}
          />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Today</p>
                <p className="text-2xl font-bold text-gray-900">{stats.todayHours.toFixed(1)}h</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">This Week</p>
                <p className="text-2xl font-bold text-gray-900">{stats.weekHours.toFixed(1)}h</p>
              </div>
              <Calendar className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">This Month</p>
                <p className="text-2xl font-bold text-gray-900">{stats.monthHours.toFixed(1)}h</p>
              </div>
              <BarChart3 className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Tasks</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalTasks}</p>
              </div>
              <FileText className="w-8 h-8 text-orange-500" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Time Entries */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Recent Time Entries</h3>
              <button
                onClick={() => navigate('/client/time-tracking')}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                View all
              </button>
            </div>
            
            {recentEntries.length === 0 ? (
              <div className="text-center py-8">
                <Timer className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No recent time entries</p>
                <p className="text-sm text-gray-400 mt-1">Start tracking time to see entries here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentEntries.map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {formatDuration(entry.duration)}
                        </span>
                        <span className="text-sm text-gray-500">
                          {new Date(entry.date).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mt-1">{entry.description}</p>
                      {entry.taskTitle && (
                        <p className="text-xs text-primary-600 mt-1">{entry.taskTitle}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tasks Overview */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">My Tasks</h3>
              <button
                onClick={() => navigate('/client/tasks')}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                View all
              </button>
            </div>
            
            {tasks.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No tasks assigned</p>
                <p className="text-sm text-gray-400 mt-1">Tasks will appear here when assigned to you</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tasks.slice(0, 5).map((task) => (
                  <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900">{task.title}</h4>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                          {task.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1">
                        <span className={`text-sm ${getPriorityColor(task.priority)}`}>
                          {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} Priority
                        </span>
                        {task.dueDate && (
                          <span className="text-sm text-gray-500">
                            Due: {new Date(task.dueDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </ClientDashboardLayout>
  );
};
